/**
 * Authority coverage in one line (migration 983; PRD-assignment-record.md §3.6, Phase 3).
 *
 * Of the binding assignments, how many were authorized by someone who held the right at
 * the time, read from the role-at-act stamp on the authorizing event. Where no one but an
 * admin holds the right, the line says so instead of reporting coverage: unverified,
 * never "covered". Nothing binding: no line. The words live in assignmentService so every
 * screen says the same thing.
 */
import { MessageSquareQuote, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { authorityCoverageWords, coverageCounts, type AuthorityCoverageRow } from '@/lib/assignmentService';
import { readbackCoverageLine } from '../../../../supabase/functions/_shared/assignmentReadbackWords.ts';

export function AuthorityCoverageLine({ row, scope, className = '' }: {
  row: AuthorityCoverageRow | null | undefined;
  scope: 'commitment' | 'workspace';
  className?: string;
}) {
  if (!row) return null;
  const counts = coverageCounts(row, scope);
  const words = authorityCoverageWords(counts, scope);
  if (!words) return null;
  const readback = readbackCoverageLine(counts.readbackUnresolved, counts.binding, scope);
  const unverified = counts.noRightConfigured + counts.unstamped > 0 || (counts.rightsConfigured === false && counts.byHolder === 0);
  const Icon = unverified ? ShieldQuestion : ShieldCheck;
  return (
    <div className={`space-y-0.5 ${className}`}>
      <div className={`flex items-start gap-1.5 text-xs ${unverified ? 'text-[var(--mustard)]' : 'text-[var(--text-sec)]'}`}>
        <Icon className="h-3.5 w-3.5 mt-px flex-shrink-0" />
        <span>{words}</span>
      </div>
      {readback && (
        <div className="flex items-start gap-1.5 text-xs text-[var(--mustard)]">
          <MessageSquareQuote className="h-3.5 w-3.5 mt-px flex-shrink-0" />
          <span>{readback}</span>
        </div>
      )}
    </div>
  );
}
